import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Paper, Alert, Snackbar, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { colors } from "../../theme/theme.js";
import {
  FileText,
  Video,
  Image as ImageIcon,
  Paperclip,
  Download,
  Eye,
  X,
  MessageSquare,
  Send,
  MoreVertical,
  Edit,
  Trash2,
  Check,
  CheckCircle2,
} from "lucide-react";

import { Card, CardContent, CardHeader } from "../../components/Card";
import { AlertNote } from "../../components/AlertNote";
import { Button } from "../../components/Button";
import { CustomMentionsInput, createMentionUsers } from "../../components/CustomMentionsInput";
import { formatContentWithMentions } from "../../components/CustomMentionsInput";
import { usePasteImage } from "../../components/hooks/usePasteImage";
import { useFileDrop } from "../../components/hooks/useFileDrop";
import { FileDropOverlay } from "../../components/FileDropOverlay";
import apiClient, {
  sendMessageWithAttachments,
  downloadAttachment,
  getAttachmentPreviewUrl,
} from "../../api-client";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { ConfirmModal } from "../../components/ConfirmModal";
import { CustomTextArea } from "../../components/CustomTextArea";
import SuccessPopup from "../../components/SuccessPopup";
import { formatFileSize } from "../../utils/fileSize";

// Helper para determinar el tipo de archivo basado en la extensión o MIME type
const getFileType = (attachment) => {
  const fileName =
    attachment.original_filename ||
    attachment.file_name ||
    attachment.name ||
    "";
  const mimeType = attachment.file_type || attachment.type || "";

  // Verificar por MIME type
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (
    mimeType.includes("pdf") ||
    mimeType.includes("document") ||
    mimeType.includes("text")
  )
    return "document";

  // Verificar por extensión
  const extension = fileName.toLowerCase().split(".").pop();
  const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
  const videoExts = ["mp4", "webm", "ogg", "avi", "mov", "mkv"];
  const docExts = ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv"];

  if (imageExts.includes(extension)) return "image";
  if (videoExts.includes(extension)) return "video";
  if (docExts.includes(extension)) return "document";

  return "other";
};

const getAttachmentIcon = (attachment) => {
  const type = getFileType(attachment);
  switch (type) {
    case "video":
      return <Video size={16} />;
    case "image":
      return <ImageIcon size={16} />;
    case "document":
      return <FileText size={16} />;
    default:
      return <Paperclip size={16} />;
  }
};

// Helper para formatear el tamaño de archivo
// Carga la imagen autenticada como object URL
const useAttachmentPreviewUrl = (attachment, fileType) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const messageId = attachment.message_id || attachment.messageId;

  useEffect(() => {
    let isActive = true;
    let objectUrl = null;

    if (fileType !== "image" || !messageId || !attachment.id) {
      setPreviewUrl(null);
      return undefined;
    }

    const loadPreview = async () => {
      try {
        objectUrl = await getAttachmentPreviewUrl(messageId, attachment.id);
        if (isActive) {
          setPreviewUrl(objectUrl);
        } else if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isActive) {
          setPreviewUrl(null);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment.id, fileType, messageId]);

  return previewUrl;
};

const getAttachmentFileName = (attachment) =>
  attachment.original_filename ||
  attachment.file_name ||
  attachment.name ||
  "Archivo sin nombre";

const ImagePreviewOverlay = ({ src, alt, onClose, onDownload }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const iconButtonSx = {
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
  };

  return createPortal(
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        cursor: "zoom-out",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 1 }}>
        {onDownload && (
          <IconButton
            title="Descargar"
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            sx={iconButtonSx}
          >
            <Download size={20} />
          </IconButton>
        )}
        <IconButton
          title="Cerrar"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={iconButtonSx}
        >
          <X size={20} />
        </IconButton>
      </Box>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          objectFit: "contain",
          borderRadius: "8px",
          cursor: "default",
        }}
      />
    </Box>,
    document.body
  );
};

// Miniatura de imagen bajo el bocadillo del mensaje
const ChatImageThumbnail = ({ attachment }) => {
  const theme = useTheme();
  const [showPreview, setShowPreview] = useState(false);
  const previewUrl = useAttachmentPreviewUrl(attachment, "image");
  const fileUrl = previewUrl || attachment.url || attachment.file || null;
  const messageId = attachment.message_id || attachment.messageId;
  const fileName = getAttachmentFileName(attachment);

  const handleDownload = async () => {
    try {
      if (messageId) {
        await downloadAttachment(messageId, attachment.id);
      } else if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error al descargar adjunto:", error);
      alert(error.message || "Error al descargar el archivo");
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        title={fileName}
        onClick={() => fileUrl && setShowPreview(true)}
        sx={{
          all: "unset",
          display: "block",
          width: 320,
          maxWidth: "100%",
          height: 180,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          backgroundColor: theme.palette.grey[100],
          cursor: fileUrl ? "zoom-in" : "default",
          transition: "opacity 150ms",
          "&:hover": { opacity: 0.9 },
        }}
      >
        {fileUrl ? (
          <img
            src={fileUrl}
            alt={fileName}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.palette.text.secondary,
            }}
          >
            <ImageIcon size={24} />
          </Box>
        )}
      </Box>

      {showPreview && fileUrl && (
        <ImagePreviewOverlay
          src={fileUrl}
          alt={fileName}
          onClose={() => setShowPreview(false)}
          onDownload={handleDownload}
        />
      )}
    </>
  );
};

// Componente para mostrar un adjunto individual en el chat
const ChatAttachmentDisplay = ({ attachment, isOtherUser = false }) => {
  const theme = useTheme();
  const [showPreview, setShowPreview] = useState(false);
  const fileType = getFileType(attachment);
  const messageId = attachment.message_id || attachment.messageId;
  const fileName =
    attachment.original_filename ||
    attachment.file_name ||
    attachment.name ||
    "Archivo sin nombre";
  const fileSize = attachment.file_size || attachment.size;
  const fallbackFileUrl = attachment.url || attachment.file || null;
  const previewUrl = useAttachmentPreviewUrl(attachment, fileType);
  const fileUrl = previewUrl || fallbackFileUrl;
  const canDownload = Boolean(messageId || fileUrl);


  const handleDownload = async () => {
    try {
      if (!messageId) {
        if (fileUrl) {
          window.open(fileUrl, "_blank");
        } else {
          throw new Error("No se pudo descargar el archivo");
        }
        return;
      }

      await downloadAttachment(messageId, attachment.id);
    } catch (error) {
      console.error("Error al descargar adjunto:", error);
      alert(error.message || "Error al descargar el archivo");
    }
  };

  const handleImageClick = () => {
    if (fileType === "image" && fileUrl) {
      setShowPreview(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          backgroundColor: isOtherUser
            ? theme.palette.action.hover
            : "rgba(147, 51, 234, 0.1)", // Lila clarito para mensajes propios
          borderRadius: "8px",
          border: `1px solid ${isOtherUser ? theme.palette.divider : "rgba(147, 51, 234, 0.2)"
            }`, // Borde lila clarito para mensajes propios
          marginBottom: "6px",
          "&:last-child": {
            marginBottom: 0,
          },
          cursor: fileType === "image" && fileUrl ? "pointer" : "default",
        }}
        onClick={handleImageClick}
      >
        {/* Vista previa o icono */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: fileType === "image" && fileUrl ? "44px" : "36px",
            height: fileType === "image" && fileUrl ? "44px" : "36px",
            borderRadius: "6px",
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {fileType === "image" && fileUrl ? (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar el icono
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <Box
            sx={{
              color: theme.palette.primary.main,
              display: fileType === "image" && fileUrl ? "none" : "flex",
            }}
          >
            {getAttachmentIcon(attachment)}
          </Box>
        </Box>

        {/* Información del archivo */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            title={fileName} // Mostrar nombre completo en tooltip
            sx={{
              fontWeight: 500,
              fontSize: "0.8rem",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginBottom: "2px",
              color: isOtherUser ? "inherit" : theme.palette.text.primary, // Negro para mensajes propios
            }}
          >
            {fileName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              lineHeight: 1.2,
              color: isOtherUser
                ? theme.palette.text.secondary
                : theme.palette.text.secondary, // Gris para otros usuarios, igual que otros
              "& span": {
                color: isOtherUser
                  ? theme.palette.text.secondary
                  : theme.palette.primary.main, // Lila para mensajes propios
              },
            }}
          >
            {formatFileSize(fileSize)} •{" "}
            {fileType === "image"
              ? "Imagen"
              : fileType === "video"
                ? "Video"
                : fileType === "document"
                  ? "Documento"
                  : "Archivo"}
            {fileType === "image" && fileUrl && (
              <span style={{ marginLeft: "8px", fontSize: "0.75rem" }}>
                Previsualizar imagen
              </span>
            )}
          </Typography>
        </Box>

        {/* Acción de descarga solo para adjuntos de otros usuarios */}
        {isOtherUser && canDownload && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
              sx={{
                minWidth: "auto",
                p: 0.5,
                color: theme.palette.text.secondary,
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                },
              }}
              title="Descargar"
            >
              <Download size={14} />
            </Button>
          </Box>
        )}
      </Box>

      {/* Modal de vista previa de imagen */}
      {showPreview && fileType === "image" && fileUrl && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={() => setShowPreview(false)}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                maxWidth: "1200px", // Límite máximo de ancho
                maxHeight: "1000px", // Límite máximo de alto
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <Button
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                minWidth: "auto",
                p: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(false);
              }}
            >
              <X size={24} />
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export function ChatInterface({
  currentUser,
  customTitle,
  customSubtitle,
  customMessages = [],
  threadId,
  participants = [], // participantes del thread (para menciones)
  onMessagesUpdate, // Prop para actualizar mensajes localmente
  onMessageSent, // Prop para refrescar mensajes en caso de error
  showThreadHeader = true,
  composerDisabled = false,
  composerDisabledMessage = "No se pueden enviar mensajes en este estado.",
  mentionsZIndex,
  mentionsSuggestionsPortal = false,
  headerActions = null,
}) {
  const theme = useTheme();
  const { colors, neutrals } = useCustomTheme();
  const [newMessage, setNewMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null); // Estado para notificaciones de error
  const messagesEndRef = useRef(null); // Referencia para hacer scroll al final

  // Estados para editar y eliminar mensajes
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  // Estados para SuccessPopup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isErrorPopup, setIsErrorPopup] = useState(false);

  // Función para mostrar notificación
  const showNotification = ({ message, isError }) => {
    setSuccessMessage(message);
    setIsErrorPopup(isError);
    setShowSuccessPopup(true);
  };

  // Función para cerrar la notificación de error
  const handleCloseError = () => {
    setError(null);
  };

  // Función para hacer scroll al final de los mensajes con animación suave
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Usar requestAnimationFrame para una animación más suave
      requestAnimationFrame(() => {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      });
    }
  };

  // Hacer scroll automático cuando cambian los mensajes
  useEffect(() => {
    // Pequeño delay para asegurar que el DOM se renderice completamente
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [customMessages]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
    });
  };

  // Fecha + hora "inteligente": Hoy/Ayer para mensajes recientes, fecha corta para el resto.
  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round(
      (startOfDay(new Date()) - startOfDay(date)) / 86400000
    );

    let label;
    if (diffDays === 0) label = "Hoy";
    else if (diffDays === 1) label = "Ayer";
    else label = formatDate(timestamp);

    return `${label} ${formatTime(timestamp)}`;
  };

  const sendMessage = async () => {
    if (composerDisabled) return;
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!threadId) {
      console.error("No thread ID provided");
      return;
    }

    try {
      setIsUploading(true);
      let response;

      if (attachments.length > 0) {
        // Usar la función de envío con adjuntos
        response = await sendMessageWithAttachments(
          threadId,
          newMessage,
          attachments,
          "normal", // message_type siempre normal para mensajes de usuario
          null // no hay consultantId en incidencias
        );
      } else {
        // Envío tradicional sin adjuntos
        const newPost = {
          thread: threadId,
          content: newMessage,
          message_type: "normal",
        };

        console.log("Enviando mensaje de incidencia:", newPost);
        response = await apiClient.post("communication/messages/", newPost);
      }

      // Limpiar formulario
      const sentMessageContent = newMessage.trim(); // Guardar el contenido antes de limpiar
      setNewMessage("");
      setAttachments([]);

      // Actualizar estado local inmediatamente con el nuevo mensaje
      if (onMessagesUpdate) {
        const newMessageData = {
          id: response?.data?.id || `temp-${Date.now()}`, // Usar ID real si está disponible, sino temporal
          content: sentMessageContent,
          created_at: new Date().toISOString(),
          sender: {
            full_name: currentUser?.full_name || currentUser?.name || "Usuario",
            role: currentUser?.role || "user",
            id: currentUser?.id, // Agregar ID del sender para la lógica de posición
          },
          message_type: "normal",
          attachments: response?.data?.attachments || [],
          is_read: true, // Marcar como leído inmediatamente ya que acabamos de enviarlo
        };
        onMessagesUpdate(newMessageData);
        // Hacer scroll al final con delay para asegurar renderizado completo
        setTimeout(() => scrollToBottom(), 150);
      } else {
        // Si no hay onMessagesUpdate, no se puede actualizar localmente
      }
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      setError("Error al enviar el mensaje. Inténtalo de nuevo.");
      // En caso de error, refrescar mensajes del backend para mantener sincronización
      if (onMessageSent) {
        onMessageSent();
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Función para iniciar la edición de un mensaje
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setEditedContent(message.content);
  };

  // Función para cancelar la edición
  const handleCancelEdit = () => {
    setEditedContent("");
    setEditingMessageId(null);
  };

  // Función para guardar la edición
  const handleSaveEdit = async () => {
    // Validar que el contenido no esté vacío
    const trimmedContent = editedContent.trim();
    if (!trimmedContent) {
      showNotification({
        message: "El contenido del mensaje no puede estar vacío",
        isError: true,
      });
      return;
    }

    try {
      const response = await apiClient.patch(
        `communication/messages/${editingMessageId}/`,
        {
          content: trimmedContent,
        }
      );

      // Actualizar el mensaje localmente
      if (onMessagesUpdate && response.data) {
        const updatedMessage = {
          ...response.data,
          updated: true, // Flag para indicar que fue actualizado
        };
        // Llamar a onMessagesUpdate con el mensaje actualizado
        onMessagesUpdate(updatedMessage, "update");
      } else if (onMessageSent) {
        // Si no hay función de actualización, refrescar todos los mensajes
        onMessageSent();
      }

      setEditedContent(trimmedContent);
      setEditingMessageId(null);

      showNotification({
        message: "Mensaje actualizado correctamente",
        isError: false,
      });
    } catch (error) {
      console.error("Error al editar mensaje:", error);
      const errorMessage =
        error?.message ||
        error.response?.data?.detail ||
        "Error al actualizar el mensaje. Intenta nuevamente.";

      showNotification({ message: errorMessage, isError: true });
    }
  };

  // Función para abrir el modal de confirmación de eliminación
  const handleOpenDeleteModal = (message) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  // Función para cerrar el modal de confirmación de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  // Función para confirmar y eliminar un mensaje
  const handleConfirmDelete = async () => {
    if (!messageToDelete) return;

    setIsDeleting(true);
    try {
      await apiClient.delete(`communication/messages/${messageToDelete.id}/`);

      // Actualizar localmente eliminando el mensaje
      if (onMessagesUpdate) {
        onMessagesUpdate(messageToDelete, "delete");
      } else if (onMessageSent) {
        // Si no hay función de actualización, refrescar todos los mensajes
        onMessageSent();
      }

      setShowDeleteModal(false);
      setMessageToDelete(null);

      showNotification({
        message: "Mensaje eliminado correctamente",
        isError: false,
      });
    } catch (error) {
      console.error("Error al eliminar mensaje:", error);
      const errorMessage =
        error?.message ||
        error.response?.data?.detail ||
        "Error al eliminar el mensaje. Intenta nuevamente.";

      setShowDeleteModal(false);
      setMessageToDelete(null);

      showNotification({ message: errorMessage, isError: true });
    } finally {
      setIsDeleting(false);
    }
  };

  const addFiles = (files) => {
    const maxFileSize = 50 * 1024 * 1024; // 50MB

    const oversizedFiles = files.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      setError(
        `Los archivos no pueden superar los 50MB. Los siguientes archivos son demasiado grandes: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`
      );
      return;
    }

    setAttachments((prev) => [...prev, ...files]);
  };

  // Función para manejar selección de archivos
  const handleFileSelection = (event) => {
    addFiles(Array.from(event.target.files));
    event.target.value = ""; // Limpiar el input
  };

  const { isDragging: isDraggingFiles, dropHandlers } = useFileDrop({
    onFiles: addFiles,
    disabled: isUploading || composerDisabled,
  });

  // Función para eliminar un archivo adjunto
  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Función para mapear mensajes de la API al formato correcto
  const mapMessage = (message) => {
    return {
      id: message.id,
      sender: message.message_type === "event" ? "system" : "agent", // eventos como system, normales como agent
      content: message.content,
      timestamp: message.created_at,
      senderName: message.sender?.full_name || "Sistema",
      senderRole: message.sender?.role || "user", // Agregar el rol del sender
      senderId: message.sender?.id, // Agregar el ID del sender
      messageType: message.message_type, // "event" o "normal"
      attachments: (message.attachments || []).map((attachment) => ({
        ...attachment,
        message_id: attachment.message_id || attachment.messageId || message.id,
      })),
    };
  };

  const messagesForConversation = (customMessages || []).map(mapMessage);
  const mentionUsers = createMentionUsers([], [], { participants }, currentUser);

  // Hook para detectar imágenes pegadas desde el portapapeles
  const handlePasteImage = usePasteImage(setAttachments, {
    onError: (msg) => setError(msg),
    disabled: isUploading || composerDisabled,
  });

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header del chat */}
      {showThreadHeader && (
        <CardHeader
          sx={{
            paddingBottom: "12px",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {customTitle || "Conversación de Ticket"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {customSubtitle || "Comunicación sobre este ticket"}
              </Typography>
            </Box>
            {headerActions ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                }}
              >
                {headerActions}
              </Box>
            ) : null}
          </Box>
        </CardHeader>
      )}

      {/* Mensajes */}
      <CardContent
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          scrollBehavior: "smooth", // Transición suave para el scroll
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[300],
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.grey[400],
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {messagesForConversation.length === 0 ? (
            <Box
              sx={{
                padding: "64px 32px",
                textAlign: "center",
                color: theme.palette.text.secondary,
              }}
            >
              <MessageSquare
                size={48}
                style={{
                  marginBottom: "12px",
                  opacity: 0.5,
                  display: "block",
                  margin: "0 auto 12px auto",
                }}
              />
              <Typography variant="body1" sx={{ marginBottom: "4px" }}>
                No hay mensajes aún
              </Typography>
              <Typography variant="body2">
                Envía el primer mensaje para comenzar la conversación
              </Typography>
            </Box>
          ) : (
            messagesForConversation.map((message) => {
              const isOwnMessage = message.senderId === currentUser?.id;
              const isEditing = editingMessageId === message.id;
              const canEdit = isOwnMessage && message.messageType === "normal";
              const isHovered = hoveredMessageId === message.id;
              const imageAttachments = message.attachments.filter(
                (attachment) => getFileType(attachment) === "image"
              );
              const fileAttachments = message.attachments.filter(
                (attachment) => getFileType(attachment) !== "image"
              );
              const hasText = Boolean(message.content?.trim());
              const showBubble =
                isEditing ||
                message.messageType !== "normal" ||
                hasText ||
                fileAttachments.length > 0;

              return (
                <Box
                  key={message.id}
                  onMouseEnter={() => setHoveredMessageId(message.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.messageType === "event"
                        ? "center"
                        : isOwnMessage
                          ? "flex-end"
                          : "flex-start",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: message.messageType === "event" ? "80%" : "60%",
                      minWidth: "auto",
                      width: "fit-content",
                      position: "relative",
                    }}
                  >
                    {showBubble && (
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius:
                          message.messageType === "event"
                            ? "8px"
                            : isOwnMessage
                              ? "8px 2px 8px 8px"
                              : "2px 8px 8px 8px",
                        padding: "10px 14px",
                        paddingRight: canEdit && !isEditing ? "42px" : "14px", // Espacio para el botón
                        width: "100%",
                        boxSizing: "border-box",
                        backgroundColor:
                          message.messageType === "event"
                            ? theme.palette.grey[100]
                            : isOwnMessage
                              ? colors.message.own
                              : neutrals.gray[50],
                        color:
                          message.messageType === "event"
                            ? theme.palette.text.secondary
                            : theme.palette.text.primary,
                        textAlign:
                          message.messageType === "event" ? "center" : "left",
                        fontStyle:
                          message.messageType === "event" ? "italic" : "normal",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                        boxShadow: "none",
                        position: "relative",
                      }}
                    >
                      {/* Menú de opciones dentro del Paper */}
                      {canEdit && !isEditing && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            zIndex: 10,
                          }}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <IconButton
                                size="small"
                                sx={{
                                  opacity: isHovered ? 1 : 0,
                                  transition: "opacity 0.2s",
                                  width: "28px",
                                  height: "28px",
                                  padding: "4px",
                                  backgroundColor: "transparent",
                                  "&:hover": {
                                    opacity: 1,
                                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                                  },
                                }}
                              >
                                <MoreVertical size={16} />
                              </IconButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleStartEdit(message)}>
                                <Edit size={16} />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleOpenDeleteModal(message)}
                                className="text-destructive"
                              >
                                <Trash2 size={16} />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </Box>
                      )}

                      {isEditing ? (
                        <Box
                          data-prevent-expand="true"
                          onClick={(event) => event.stopPropagation()}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            cursor: "default",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <CustomTextArea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            placeholder="Escribe tu mensaje..."
                            autoFocus
                            rows={1}
                            maxRows={6}
                            autoExpand={true}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelEdit();
                              }
                            }}
                            sx={{
                              flex: 1,
                              width: "100%",
                              minHeight: "32px",
                              lineHeight: 1.4,
                              fontSize: "0.9rem",
                            }}
                          />
                          <Box
                            data-prevent-expand="true"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <IconButton
                              size="small"
                              data-prevent-expand="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSaveEdit();
                              }}
                              title="Guardar"
                              disabled={!editedContent.trim()}
                            >
                              <CheckCircle2 size={16} />
                            </IconButton>
                            <IconButton
                              size="small"
                              data-prevent-expand="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCancelEdit();
                              }}
                              title="Cancelar"
                            >
                              <X size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.4,
                              width: "100%",
                              margin: 0,
                              padding: 0,
                            }}
                          >
                            {formatContentWithMentions(message.content, theme)}
                          </Typography>

                          {/* Mostrar adjuntos si existen (solo para mensajes normales) */}
                          {message.messageType === "normal" &&
                            fileAttachments.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {fileAttachments.map((attachment) => (
                                  <ChatAttachmentDisplay
                                    key={attachment.id}
                                    attachment={attachment}
                                    isOtherUser={!isOwnMessage}
                                  />
                                ))}
                              </Box>
                            )}
                        </>
                      )}
                    </Paper>
                    )}

                    {message.messageType === "normal" && imageAttachments.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwnMessage ? "flex-end" : "flex-start",
                          gap: "6px",
                          mt: showBubble ? "6px" : 0,
                        }}
                      >
                        {imageAttachments.map((attachment) => (
                          <ChatImageThumbnail key={attachment.id} attachment={attachment} />
                        ))}
                      </Box>
                    )}

                    {/* Solo mostrar información del sender para mensajes normales */}
                    {message.messageType === "normal" && !isEditing && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          marginTop: "2px",
                          color: theme.palette.text.secondary,
                          textAlign: isOwnMessage ? "right" : "left",
                          fontSize: "0.75rem",
                        }}
                      >
                        {message.senderName} • {formatDateTime(message.timestamp)}
                      </Typography>
                    )}

                    {/* Para eventos, mostrar fecha y hora */}
                    {message.messageType === "event" && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          marginTop: "2px",
                          color: theme.palette.text.secondary,
                          textAlign: "center",
                          fontSize: "0.75rem",
                        }}
                      >
                        {formatDateTime(message.timestamp)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        {/* Elemento invisible para hacer scroll al final */}
        <div ref={messagesEndRef} />
      </CardContent>

      {/* Input para nuevo mensaje */}
      <Box
        {...dropHandlers}
        sx={{
          position: "relative",
          padding: "16px",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {isDraggingFiles && <FileDropOverlay />}
        {composerDisabled && (
          <AlertNote variant="warning" sx={{ mb: 2 }}>
            {composerDisabledMessage}
          </AlertNote>
        )}

        {/* Mostrar archivos adjuntos seleccionados */}
        {attachments.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mb: 1,
                display: "block",
              }}
            >
              Archivos adjuntos:
            </Typography>
            {attachments.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  p: 1,
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: 1,
                }}
              >
                <Paperclip size={14} />
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeAttachment(index)}
                  disabled={composerDisabled}
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                  }}
                >
                  ×
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
          {/* Input oculto para seleccionar archivos */}
          <input
            type="file"
            id="file-input"
            multiple
            onChange={handleFileSelection}
            style={{ display: "none" }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => document.getElementById("file-input").click()}
            disabled={isUploading || composerDisabled}
            sx={{
              width: "40px",
              height: "40px",
              minWidth: "40px",
              padding: 0,
            }}
          >
            <Paperclip size={16} />
          </Button>
          <CustomMentionsInput
            placeholder="Escribe tu mensaje..."
            value={newMessage}
            onChange={(event, newValue) => setNewMessage(newValue)}
            users={mentionUsers}
            useIncidentsChatStyle={true}
            suggestionsZIndex={mentionsZIndex}
            suggestionsPortal={mentionsSuggestionsPortal}
            sx={{
              flex: 1,
              minHeight: "40px",
              maxHeight: "280px",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                e.preventDefault();
                if (!composerDisabled) {
                  sendMessage();
                }
              }
            }}
            onPaste={handlePasteImage}
            disabled={isUploading || composerDisabled}
          />
          <Button
            onClick={sendMessage}
            size="sm"
            disabled={
              composerDisabled ||
              (!newMessage.trim() && attachments.length === 0) ||
              isUploading
            }
            sx={{
              width: "40px",
              height: "40px",
              minWidth: "40px",
              padding: 0,
            }}
          >
            <Send size={16} />
          </Button>
        </Box>

        {/* Notificación de error en línea */}
        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ marginTop: "12px" }}
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* Modal de confirmación para eliminar mensaje */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Eliminar mensaje"
        question="¿Estás seguro de que deseas eliminar este mensaje?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el mensaje."
        confirmLabel="Eliminar mensaje"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />

      {/* Popup de éxito/error */}
      <SuccessPopup
        message={successMessage}
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        error={isErrorPopup}
      />
    </Box>
  );
}
